import { useRef, useMemo } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Check, ArrowRight, Star, Phone } from 'lucide-react';
import { bookingLinks } from '../../data/checkoutLinks';

const packagesData = {
  artist: [
    {
      id: 'artist-01',
      formula: '01',
      name: 'FORMULA 01',
      subtitle: 'Single Release Package',
      price: 250,
      turnaround: '5-7 business days',
      features: [
        { text: 'Single Cover Design', isSubcategory: false },
        { text: 'YouTube Thumbnail Design', isSubcategory: false },
        { text: 'Digital Song Promotion Flyer', isSubcategory: false },
        { text: 'Post Version', isSubcategory: true },
        { text: 'Story Version', isSubcategory: true }
      ]
    },
    {
      id: 'artist-02',
      formula: '02',
      name: 'FORMULA 02',
      subtitle: 'Album / EP Package',
      price: 550,
      turnaround: '5-7 business days',
      featured: true,
      features: [
        { text: 'Album / EP Cover Design (Front + Back Tracklist)', isSubcategory: false },
        { text: 'YouTube Thumbnails (multiple songs)', isSubcategory: false },
        { text: 'Digital Album Promotion Flyer', isSubcategory: false },
        { text: 'Post Version', isSubcategory: true },
        { text: 'Story Version', isSubcategory: true },
        { text: 'Official Artist Logo Design + Brand Board', isSubcategory: false }
      ]
    },
    {
      id: 'artist-03',
      formula: '03',
      name: 'FORMULA 03',
      subtitle: 'Full Campaign Package',
      price: 1600,
      turnaround: '3-5 business days',
      features: [
        { text: 'Landing Page (EPK)', isSubcategory: false },
        { text: 'Large Format Promo Design', isSubcategory: false },
        { text: 'Billboard Design', isSubcategory: true, highlight: true },
        { text: 'Artist Poster Design', isSubcategory: true },
        { text: 'Event Banner', isSubcategory: true },
        { text: 'Merch Drop Mockup Board', isSubcategory: false },
        { text: 'Promotional Blog Write-up', isSubcategory: false },
        { text: 'QR Code Generation (Included)', isSubcategory: false }
      ]
    }
  ],
  business: [
    {
      id: 'business-01',
      formula: '01',
      name: 'FORMULA 01',
      subtitle: 'Brand Starter Package',
      price: 350,
      turnaround: '5-7 business days',
      features: [
        { text: 'Logo Design + Icon (logo cleanup optional)', isSubcategory: false },
        { text: 'Social Media Launch Kit', isSubcategory: false },
        { text: 'Profile AVI (Various Backgrounds)', isSubcategory: true },
        { text: 'Social Headers (Facebook, X, LinkedIn, YouTube sizes)', isSubcategory: true },
        { text: 'Business Brand Board', isSubcategory: false }
      ]
    },
    {
      id: 'business-02',
      formula: '02',
      name: 'FORMULA 02',
      subtitle: 'Marketing Essentials Package',
      price: 500,
      turnaround: '5-7 business days',
      featured: true,
      features: [
        { text: 'Business Flyer Design (Print + Digital)', isSubcategory: false },
        { text: 'Print (Front: Logo | Back: Services)', isSubcategory: true },
        { text: 'Post Version', isSubcategory: true },
        { text: 'Story Version', isSubcategory: true },
        { text: 'Business Card Design (Front + Back)', isSubcategory: false },
        { text: '2 Product Highlight Post Designs', isSubcategory: false },
        { text: 'Post + Story', isSubcategory: true }
      ]
    },
    {
      id: 'business-03',
      formula: '03',
      name: 'FORMULA 03',
      subtitle: 'Business Growth Package',
      price: 1600,
      turnaround: '3-5 business days',
      features: [
        { text: 'Landing Page (Website)', isSubcategory: false },
        { text: 'Large Format Promo Design', isSubcategory: false },
        { text: 'Billboard Design', isSubcategory: true, highlight: true },
        { text: 'Event Banners', isSubcategory: true },
        { text: 'Storefront Window Decal', isSubcategory: true },
        { text: 'Merch Design Mockup Board', isSubcategory: false },
        { text: 'Promotional Blog Write-up', isSubcategory: false }
      ]
    }
  ],
  events: [
    {
      id: 'events-01',
      formula: '01',
      name: 'FORMULA 01',
      subtitle: 'Event Flyer Package',
      price: 150,
      turnaround: '3-5 business days',
      features: [
        { text: 'Event Flyer Design', isSubcategory: false },
        { text: 'Post Version', isSubcategory: true },
        { text: 'Story Version', isSubcategory: true }
      ]
    },
    {
      id: 'events-02',
      formula: '02',
      name: 'FORMULA 02',
      subtitle: 'Ongoing Venue / Promoter Support',
      price: null,
      callOnly: true,
      description: 'Ongoing design support for clubs and promoters running recurring events.',
      features: [
        { text: 'Designed for weekly or monthly event schedules that require:', isSubcategory: false, isHeader: true },
        { text: 'Consistent event flyer designs', isSubcategory: false },
        { text: 'Post + story versions for each event', isSubcategory: false },
        { text: 'Reliable turnaround for recurring nights', isSubcategory: false },
        { text: 'Support for schedule or lineup changes', isSubcategory: false }
      ]
    },
    {
      id: 'events-03',
      formula: '03',
      name: 'FORMULA 03',
      subtitle: 'Large-Scale Event Campaign',
      price: null,
      callOnly: true,
      description: 'High-level visual support for large, coordinated events that require clear communication at scale.',
      features: [
        { text: 'Designed for:', isSubcategory: false, isHeader: true },
        { text: 'Churches & funeral services', isSubcategory: false },
        { text: 'Weddings & formal ceremonies', isSubcategory: false },
        { text: 'Schools & educational events', isSubcategory: false },
        { text: 'Speaking engagements & conferences', isSubcategory: false },
        { text: 'Festivals & large public events', isSubcategory: false }
      ]
    }
  ]
};

const ScenePackages = ({ selectedIdentity, selectedPackage, onSelectPackage }) => {
  const sectionRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.15, 0.85, 1], [0, 1, 1, 0]);
  
  const packages = useMemo(() => {
    if (!selectedIdentity) return null;
    return packagesData[selectedIdentity] || packagesData.artist;
  }, [selectedIdentity]);

  if (!selectedIdentity) {
    return (
      <section 
        ref={sectionRef}
        className="scene relative min-h-[50vh] flex items-center justify-center py-24"
        id="packages"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-background to-frmla-dark" />
        <motion.div 
          className="relative z-10 text-center px-6"
          style={{ opacity }}
        >
          <p className="font-body text-muted-foreground tracking-wider">
            ↑ Select your path above to reveal packages
          </p>
        </motion.div>
      </section>
    );
  }

  return (
    <section 
      ref={sectionRef}
      className="scene relative min-h-screen py-24 overflow-hidden"
      id="packages"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-frmla-dark/80 to-background" />
      
      <motion.div 
        className="relative z-10 w-full max-w-7xl mx-auto px-6"
        style={{ opacity }}
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="font-body text-sm tracking-[0.3em] text-accent/80 uppercase mb-4">
            PACKAGES
          </p>
          <h2 className="font-display text-hero text-foreground">
            CHOOSE YOUR LEVEL
          </h2>
        </motion.div>
        
        {/* Package cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-6">
          {packages.map((pkg, index) => {
            const isSelected = selectedPackage?.id === pkg.id;
            const isFeatured = pkg.featured;
            
            return (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.7, 
                  delay: index * 0.1,
                  ease: [0.4, 0, 0.2, 1]
                }}
                viewport={{ once: true }}
                className={`package-card flex flex-col ${isFeatured ? 'package-card-featured' : ''} ${isSelected ? 'ring-1 ring-accent/60' : ''}`}
              >
                {/* Package name */}
                <div className="p-6">
                  <h3 className="font-display text-2xl md:text-3xl text-foreground mb-1">
                    {pkg.name}
                  </h3>
                  <p className="font-body text-sm text-muted-foreground">
                    {pkg.subtitle}
                  </p>
                </div>
                
                {/* Gold divider */}
                <div className="gold-line-solid mx-6" />
                
                {/* Features */}
                <div className="p-6 flex-grow">
                  <ul className="space-y-2">
                    {pkg.features.map((feature, i) => {
                      const isSubcategory = feature.isSubcategory;
                      const isHighlight = feature.highlight;
                      
                      return (
                        <li 
                          key={i} 
                          className={`flex items-start gap-3 ${isSubcategory ? 'ml-7' : ''}`}
                        >
                          {!isSubcategory && !feature.isHeader && !feature.isNote && (
                            <Check className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                          )}
                          {isSubcategory && (
                            <span className="text-muted-foreground/50 mt-0.5 flex-shrink-0">–</span>
                          )}
                          <span className={`font-body text-sm ${
                            feature.isHeader
                              ? 'text-muted-foreground/80 italic'
                              : feature.isNote
                                ? 'text-muted-foreground/60 text-xs mt-2'
                                : isHighlight 
                                  ? 'text-accent font-medium' 
                                  : isSubcategory 
                                    ? 'text-muted-foreground/70' 
                                    : 'text-muted-foreground'
                          }`}>
                            {feature.text}
                            {isHighlight && <Star className="inline w-3 h-3 ml-1 text-accent" />}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                
                {/* Description for call-only packages */}
                {pkg.description && (
                  <div className="px-6 pb-4">
                    <p className="font-body text-sm text-muted-foreground/70">
                      {pkg.description}
                    </p>
                  </div>
                )}
                
                {/* Turnaround - only show if not call-only */}
                {!pkg.callOnly && pkg.turnaround && (
                  <div className="px-6 pb-4">
                    <p className="font-body text-xs text-accent">
                      Turnaround: <span className="text-muted-foreground">{pkg.turnaround}</span>
                    </p>
                  </div>
                )}
                
                {/* Price & CTA */}
                <div className="p-6 pt-0 mt-auto">
                  {/* Price - only show if has price */}
                  {pkg.price !== null && (
                    <div className="flex items-end justify-between mb-4">
                      <span className="font-display text-5xl text-foreground">
                        ${pkg.price}
                      </span>
                    </div>
                  )}
                  
                  {/* CTA Button */}
                  {pkg.callOnly ? (
                    <a
                      href={bookingLinks.call_only}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-4 font-display text-sm tracking-wider transition-all duration-300 bg-secondary text-foreground hover:bg-accent hover:text-background flex items-center justify-center gap-2"
                    >
                      <Phone className="w-4 h-4" /> SCHEDULE A CALL
                    </a>
                  ) : (
                    <button
                      onClick={() => onSelectPackage(pkg)}
                      className={`w-full py-4 font-display text-sm tracking-wider transition-all duration-300 ${
                        isSelected 
                          ? 'bg-accent text-background' 
                          : 'bg-foreground text-background hover:bg-accent'
                      }`}
                    >
                      {isSelected ? 'SELECTED' : 'START THIS PACKAGE'} <ArrowRight className="inline w-4 h-4 ml-2" />
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </section>
  );
};

export default ScenePackages;
